'use client'

import { useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'

const upcomingLots = [
  {
    id: 1,
    artist: 'Yayoi Kusama',
    title: 'Infinity Nets (TWOWQ)',
    house: "Christie's",
    location: 'New York',
    date: '2024-03-15',
    estimateLow: 800000,
    estimateHigh: 1200000,
    currency: 'USD',
    medium: 'Acrylic on canvas',
    year: 2019,
    signal: 'strong',
    signalText: 'Above estimate likely',
    comparables: 12,
    avgHammer: 1.34,
    tracked: true,
  },
  {
    id: 2,
    artist: 'Gerhard Richter',
    title: 'Abstraktes Bild',
    house: "Sotheby's",
    location: 'London',
    date: '2024-03-18',
    estimateLow: 2000000,
    estimateHigh: 3000000,
    currency: 'GBP',
    medium: 'Oil on canvas',
    year: 1992,
    signal: 'neutral',
    signalText: 'At estimate',
    comparables: 34,
    avgHammer: 1.02,
    tracked: false,
  },
  {
    id: 3,
    artist: 'Julie Mehretu',
    title: 'Stadia II',
    house: 'Phillips',
    location: 'New York',
    date: '2024-03-20',
    estimateLow: 500000,
    estimateHigh: 700000,
    currency: 'USD',
    medium: 'Ink and acrylic on canvas',
    year: 2004,
    signal: 'strong',
    signalText: 'High demand',
    comparables: 8,
    avgHammer: 1.67,
    tracked: true,
  },
  {
    id: 4,
    artist: 'Yoshitomo Nara',
    title: 'Thinking About the Future',
    house: "Christie's",
    location: 'Hong Kong',
    date: '2024-03-22',
    estimateLow: 4000000,
    estimateHigh: 6000000,
    currency: 'HKD',
    medium: 'Acrylic on canvas',
    year: 2018,
    signal: 'hot',
    signalText: 'Exceptional demand',
    comparables: 15,
    avgHammer: 2.1,
    tracked: false,
  },
]

const stats = [
  { value: '12,847', label: 'Lots upcoming', sublabel: 'Next 30 days' },
  { value: '186', label: 'Auction houses', sublabel: 'Worldwide coverage' },
  { value: '2.4s', label: 'Alert speed', sublabel: 'From listing to inbox' },
]

function formatCurrency(value: number, currency: string) {
  const symbols: Record<string, string> = { USD: '$', GBP: '£', EUR: '€', HKD: 'HK$' }
  const symbol = symbols[currency] || currency
  if (value >= 1000000) {
    return `${symbol}${(value / 1000000).toFixed(1)}M`
  }
  return `${symbol}${(value / 1000).toFixed(0)}K`
}

function formatDate(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function daysUntil(dateString: string) {
  const date = new Date(dateString)
  const now = new Date()
  const diff = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  return diff
}

function SignalBadge({ signal, text }: { signal: string; text: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    hot: { bg: 'rgba(239, 68, 68, 0.1)', text: 'var(--negative)' },
    strong: { bg: 'rgba(34, 197, 94, 0.1)', text: 'var(--positive)' },
    neutral: { bg: 'rgba(156, 163, 175, 0.1)', text: 'var(--text-tertiary)' },
  }
  const style = colors[signal] || colors.neutral

  return (
    <span
      className="text-xs px-2 py-1 rounded-full font-medium"
      style={{ background: style.bg, color: style.text }}
    >
      {text}
    </span>
  )
}

export function UpcomingAuctions() {
  const [trackedArtists, setTrackedArtists] = useState<number[]>([1, 3])

  const toggleTrack = (id: number) => {
    setTrackedArtists(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  return (
    <section id="alerts" className="py-24 px-6 bg-card">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-3 h-3 rounded-full animate-pulse"
                style={{ background: 'var(--positive)' }}
              />
              <span className="text-sm font-medium" style={{ color: 'var(--positive)' }}>
                Live tracking
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight mb-2">
              Never miss a lot
            </h2>
            <p className="text-text-secondary max-w-xl">
              Every upcoming auction, worldwide. Get alerts the moment tracked artists come to market—with
              historical context already attached.
            </p>
          </div>

          {/* Stats */}
          <div className="flex gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-right">
                <div className="text-2xl font-display font-light tabular-nums">{stat.value}</div>
                <div className="text-xs text-text-tertiary">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Alert preview banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="rounded-2xl p-6 mb-8 relative overflow-hidden"
          style={{ background: 'var(--hero-ground)' }}
        >
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-center gap-4 flex-1">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.1)' }}
              >
                <svg className="w-6 h-6" style={{ color: 'var(--accent-warm)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <div>
                <p className="font-medium" style={{ color: 'var(--accent-cream)' }}>
                  New alert: Yayoi Kusama coming to auction
                </p>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  Christie's New York · March 15 · Est. $800K–1.2M · 12 comparables suggest above-estimate result
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                className="pill text-sm"
                style={{ background: 'var(--accent-cream)', color: 'var(--hero-ground)' }}
              >
                View lot
              </button>
              <button
                className="pill text-sm"
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: 'var(--accent-cream)' }}
              >
                Set bid alert
              </button>
            </div>
          </div>
        </motion.div>

        {/* Upcoming lots grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {upcomingLots.map((lot, i) => (
            <motion.div
              key={lot.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              viewport={{ once: true }}
              className="group card p-6 relative overflow-hidden transition-all duration-300 hover:shadow-lg"
            >
              {/* Tracked indicator */}
              {trackedArtists.includes(lot.id) && (
                <div
                  className="absolute top-0 left-0 right-0 h-0.5"
                  style={{ background: 'var(--accent-primary)' }}
                />
              )}

              {/* Header row */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-display text-xl font-light mb-1">{lot.artist}</h3>
                  <p className="text-sm text-text-secondary italic">{lot.title}, {lot.year}</p>
                </div>
                <button
                  onClick={() => toggleTrack(lot.id)}
                  className={`p-2 rounded-lg transition-colors ${
                    trackedArtists.includes(lot.id)
                      ? 'bg-accent-primary/10 text-accent-primary'
                      : 'bg-page text-text-tertiary hover:text-text-secondary'
                  }`}
                >
                  <svg className="w-5 h-5" fill={trackedArtists.includes(lot.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </button>
              </div>

              {/* Auction details */}
              <div className="flex items-center gap-4 mb-4 text-sm">
                <span className="text-text-secondary">{lot.house}</span>
                <span className="text-text-muted">·</span>
                <span className="text-text-secondary">{lot.location}</span>
                <span className="text-text-muted">·</span>
                <span className="font-medium" style={{ color: daysUntil(lot.date) <= 7 ? 'var(--accent-warm)' : 'var(--text-primary)' }}>
                  {formatDate(lot.date)}
                  <span className="text-text-tertiary font-normal ml-1">
                    ({daysUntil(lot.date)}d)
                  </span>
                </span>
              </div>

              {/* Estimate and signal */}
              <div className="flex items-end justify-between mb-4">
                <div>
                  <p className="text-xs text-text-tertiary mb-1">Estimate</p>
                  <p className="text-2xl font-display font-light tabular-nums">
                    {formatCurrency(lot.estimateLow, lot.currency)}–{formatCurrency(lot.estimateHigh, lot.currency)}
                  </p>
                </div>
                <SignalBadge signal={lot.signal} text={lot.signalText} />
              </div>

              {/* AI context */}
              <div className="pt-4 border-t border-border-subtle">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    <span className="text-text-tertiary">
                      <span className="font-medium text-text-secondary">{lot.comparables}</span> comparables
                    </span>
                    <span className="text-text-tertiary">
                      Avg hammer: <span className="font-medium" style={{ color: lot.avgHammer > 1 ? 'var(--positive)' : 'var(--text-secondary)' }}>{lot.avgHammer}×</span> est.
                    </span>
                  </div>
                  <Link
                    href="#"
                    className="text-sm font-medium hover:underline"
                    style={{ color: 'var(--accent-primary)' }}
                  >
                    Full analysis →
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Feature highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            {
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              ),
              title: 'Global coverage',
              description: "Every major and regional auction house. From Christie's to local estates.",
            },
            {
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              ),
              title: 'Instant alerts',
              description: 'Email, SMS, or push. The moment a tracked artist is listed, you know.',
            },
            {
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
              title: 'Pre-attached context',
              description: 'Every alert includes comparables, price history, and AI-powered estimates.',
            },
          ].map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="p-6 rounded-xl bg-page border border-border"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ background: 'var(--card)', color: 'var(--accent-primary)' }}
              >
                {feature.icon}
              </div>
              <h3 className="font-medium mb-2">{feature.title}</h3>
              <p className="text-sm text-text-secondary">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="border-t border-border pt-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="text-text-secondary text-sm mb-1">
                Track unlimited artists. Get alerts before everyone else.
              </p>
              <p className="text-text-tertiary text-xs">
                Average alert delivery: 2.4 seconds from listing
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                href="/get-started"
                className="pill"
                style={{ background: 'var(--accent-primary)', color: 'white' }}
              >
                Start tracking artists
              </Link>
              <Link
                href="/app/market"
                className="pill flex items-center gap-2"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-standard)',
                  color: 'var(--text-primary)'
                }}
              >
                Browse upcoming auctions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
